import React, { useState } from 'react';
import { 
  Avatar, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Typography, 
  Box, 
  Chip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person,
  Dashboard,
  Settings,
  Folder,
  Cloud,
  Inventory2,
  People,
  Security,
  Logout,
  AdminPanelSettings
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNavigate } from 'react-router-dom';

function UserAvatar() {
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const logout = auth?.logout;
  const canViewInventory = auth?.canViewInventory?.() || false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Safe function access with defensive checks
  const checkIsAdmin = () => {
    if (!auth || typeof auth.isAdmin !== 'function') return false;
    try {
      return auth.isAdmin();
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };
  
  const checkIsSysAdmin = () => {
    if (!auth || typeof auth.isSysAdmin !== 'function') return false;
    try {
      return auth.isSysAdmin();
    } catch (error) {
      console.error('Error checking sysadmin status:', error);
      return false;
    }
  };
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();
  
  if (!currentUser) return null;
  
  // Get user's initials for the avatar
  const initials = currentUser.name
    ? currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    : currentUser.email
    ? currentUser.email.substring(0, 2).toUpperCase()
    : 'U';
  
  // Handle logout action
  const handleLogout = async () => {
    if (typeof logout !== 'function') return;
    
    setLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoggingOut(false);
      setAnchorEl(null);
    }
  };
  
  // Navigate to a specific route and close menu
  const navigateTo = (path) => {
    navigate(path);
    setAnchorEl(null);
  };
  
  // Get highest role for display
  const getHighestRole = () => {
    const roles = currentUser?.roles || [];
    if (roles.includes('SuperAdmin') || roles.includes('SysAdmin')) return { label: 'SuperAdmin', color: 'error' };
    if (roles.includes('Admin')) return { label: 'Admin', color: 'warning' };
    if (roles.includes('PowerUser')) return { label: 'PowerUser', color: 'info' };
    if (roles.includes('ComplianceUser') || roles.includes('Compliance User')) return { label: 'Compliance User', color: 'secondary' };
    if (roles.includes('InventorySensitiveViewer')) return { label: 'Sensitive Inventory', color: 'secondary' };
    if (roles.includes('InventoryViewer')) return { label: 'Inventory', color: 'primary' };
    if (roles.includes('FinancialsViewer')) return { label: 'Financials', color: 'success' };
    if (roles.includes('ReadOnly') || roles.includes('User')) return { label: 'ReadOnly', color: 'success' };
    return { label: 'User', color: 'default' };
  };

  // Get background color based on role
  const getAvatarColor = () => {
    if (checkIsSysAdmin()) return theme.palette.error.main;
    if (checkIsAdmin()) return theme.palette.warning.main;
    const roles = currentUser?.roles || [];
    if (roles.includes('PowerUser')) return theme.palette.info.main;
    return theme.palette.primary.main;
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Avatar
          sx={{
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            bgcolor: getAvatarColor(),
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 600,
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.shadows[2]
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: { xs: 280, sm: 320 },
            mt: 1,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
            '& .MuiMenuItem-root': {
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
          >
            {currentUser.name || 'User'}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              mb: 1,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            {currentUser.email || 'No email'}
          </Typography>
          <Chip
            label={getHighestRole().label}
            size="small"
            color={getHighestRole().color}
            variant="outlined"
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 20, sm: 24 }
            }}
          />
        </Box>
        
        <Divider />
        
        {/* Navigation Items */}
        <MenuItem onClick={() => navigateTo('/profile')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="My Profile"
            primaryTypographyProps={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </MenuItem>
        
        <MenuItem onClick={() => navigateTo('/')}>
          <ListItemIcon>
            <Dashboard fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Dashboard"
            primaryTypographyProps={{
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </MenuItem>

        {canViewInventory && (
          <MenuItem onClick={() => navigateTo('/inventory')}>
            <ListItemIcon>
              <Inventory2 fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Inventory"
              primaryTypographyProps={{
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            />
          </MenuItem>
        )}
        
        {/* Admin Menu Items */}
        {checkIsAdmin() && (
          <>
            <Divider />
            <MenuItem onClick={() => navigateTo('/admin')}>
              <ListItemIcon>
                <AdminPanelSettings fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Dashboard"
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </MenuItem>
            
            <MenuItem onClick={() => navigateTo('/admin/programs')}>
              <ListItemIcon>
                <Folder fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Manage Programs"
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </MenuItem>
            
            <MenuItem onClick={() => navigateTo('/admin/cloud-accounts')}>
              <ListItemIcon>
                <Cloud fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Cloud Accounts"
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </MenuItem>
          </>
        )}
        
        {/* SysAdmin Menu Items */}
        {checkIsSysAdmin() && (
          <>
            <Divider />
            <MenuItem onClick={() => navigateTo('/admin/users')}>
              <ListItemIcon>
                <People fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="User Management"
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </MenuItem>
            
            <MenuItem onClick={() => navigateTo('/admin/saml')}>
              <ListItemIcon>
                <Security fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="SAML Configuration"
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              />
            </MenuItem>
          </>
        )}
        
        <Divider />
        
        {/* Logout Item */}
        <MenuItem 
          onClick={handleLogout}
          disabled={loggingOut || typeof logout !== 'function'}
          sx={{
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.main + '10'
            }
          }}
        >
          <ListItemIcon>
            <Logout 
              fontSize="small" 
              sx={{ color: 'inherit' }}
            />
          </ListItemIcon>
          <ListItemText 
            primary={loggingOut ? 'Signing out...' : 'Sign Out'}
            primaryTypographyProps={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              color: 'inherit'
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}

export default UserAvatar;
